import React, { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../Firebase/Config";
import { collection, addDoc } from "firebase/firestore";
import {
  TextField,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Chip,
  InputAdornment,
  Avatar,
  IconButton,
  useTheme
} from "@mui/material";
import {
  Business,
  LocationOn,
  Phone,
  AssignmentInd,
  CheckCircle,
  Close,
  ArrowForward,
  Add,
  Link,
  Info,
  EmojiEmotions,
  Description,
  PhotoCamera,
  VerifiedUser
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Autocomplete } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const UsageGuide = ({ open, onClose, onStart }) => {
  const [guideStep, setGuideStep] = useState(0);
  const theme = useTheme();

  const steps = [
    {
      icon: <EmojiEmotions fontSize="large" />,
      title: "Bienvenue dans l'Assistant de Création",
      content: "Découvrez comment ajouter votre établissement en quelques minutes",
      illustration: "👨🍳"
    },
    {
      icon: <Description fontSize="large" />,
      title: "Informations de Base",
      content: [
        "Nom officiel de l'établissement",
        "Adresse exacte",
        "Coordonnées vérifiables",
        "Matricule fiscale valide"
      ],
      illustration: "📄"
    },
    {
      icon: <PhotoCamera fontSize="large" />,
      title: "Image & Succursales",
      content: [
        "URL d'image haute qualité",
        "Format JPG/PNG sécurisé (HTTPS)",
        "Ajout des différentes succursales",
        "Gestion des localisations"
      ],
      illustration: "🏢"
    },
    {
      icon: <VerifiedUser fontSize="large" />,
      title: "Validation Finale",
      content: [
        "Vérification des informations",
        "Confirmation en un clic",
        "Certification automatique",
        "Publication immédiate"
      ],
      illustration: "🎉"
    }
  ];

  const ProgressDots = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, my: 3 }}>
      {steps.map((_, index) => (
        <Box
          key={index}
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            bgcolor: index === guideStep ? theme.palette.primary.main : theme.palette.divider,
            transition: 'all 0.3s ease'
          }}
        />
      ))}
    </Box>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, #f8f9fa 100%)`,
          minWidth: 500
        }
      }}
    >
      <Box sx={{ p: 4, position: 'relative' }}>
        <IconButton 
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            right: 16, 
            top: 16,
            '&:hover': {
              bgcolor: theme.palette.action.hover
            }
          }}
        >
          <Close />
        </IconButton>

        <ProgressDots />

        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          minHeight: 400
        }}>
          <Box sx={{
            width: 120,
            height: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: theme.palette.primary.light,
            borderRadius: '50%',
            mb: 4,
            fontSize: '3.5rem',
            boxShadow: theme.shadows[4]
          }}>
            {steps[guideStep].illustration}
          </Box>

          <Typography variant="h5" component="div" sx={{ 
            fontWeight: 700,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            {steps[guideStep].icon}
            {steps[guideStep].title}
          </Typography>

          {Array.isArray(steps[guideStep].content) ? (
            <Box component="ul" sx={{ 
              textAlign: 'left',
              pl: 0,
              mb: 4,
              '& li': {
                listStyle: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                background: theme.palette.mode === 'light' ? 
                  'rgba(0, 0, 0, 0.03)' : 
                  'rgba(255, 255, 255, 0.04)'
              }
            }}>
              {steps[guideStep].content.map((item, index) => (
                <li key={index}>
                  <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="body1">{item}</Typography>
                </li>
              ))}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ 
              mb: 4,
              color: theme.palette.text.secondary,
              lineHeight: 1.6
            }}>
              {steps[guideStep].content}
            </Typography>
          )}

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            width: '100%',
            mt: 'auto'
          }}>
            <Button
              disabled={guideStep === 0}
              onClick={() => setGuideStep(s => s - 1)}
              sx={{
                visibility: guideStep === 0 ? 'hidden' : 'visible',
                minWidth: 120
              }}
            >
              Précédent
            </Button>

            {guideStep < steps.length - 1 ? (
              <Button 
                variant="contained" 
                onClick={() => setGuideStep(s => s + 1)}
                endIcon={<ArrowForward />}
                sx={{ 
                  minWidth: 120,
                  borderRadius: 50,
                  ml: 'auto'
                }}
              >
                Suivant
              </Button>
            ) : (
              <Button 
                variant="contained" 
                color="success"
                onClick={() => {
                  onClose();
                  if (onStart) onStart();
                }}
                startIcon={<CheckCircle />}
                sx={{ 
                  borderRadius: 50,
                  px: 4
                }}
              >
                Commencer
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

const tunisiaAddresses = [
  "Tunis",
  "Sfax",
  "Sousse",
  "Kairouan",
  "Bizerte",
  "Gabès",
  "Ariana",
  "Gafsa",
  "Monastir",
  "Nabeul",
  "Ben Arous",
  "Mahdia",
  "Jendouba",
  "Tozeur",
  "Kebili",
  "Medenine",
  "Tataouine",
  "Kasserine",
  "Siliana",
];

const AddRestaurantFormPopup = ({ open, onClose, user: propUser }) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [matriculeFiscale, setMatriculeFiscale] = useState("");
  const [imageUrl, setImageUrl] = useState("https://www.leparisien.fr/resizer/eaSb-oLv-V9fDwjANeK5jnnPEas=/932x582/cloudfront-eu-central-1.images.arcpublishing.com/leparisien/ADZL22XDE5ALXIQXIC344M4LCA.jpg");
  
  const [localInput, setLocalInput] = useState("");
  const [message, setMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [currentUser, setCurrentUser] = useState(propUser || null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();
  const [guideOpen, setGuideOpen] = useState(false);
  
  // Get restaurant ID from localStorage
  const restaurantId = localStorage.getItem("restaurantId");

  const steps = ["Détails du restaurant", "Image & Succursales", "Terminé"];

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user || propUser);
    });
    
    // Automatically open guide if no restaurant ID exists and dialog is open
    if (!restaurantId && open) {
      setGuideOpen(true);
    }
    
    return unsubscribe;
  }, [restaurantId, open, propUser]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName("");
      setAddress("");
      setPhone("");
      setMatriculeFiscale("");
      setImageUrl("https://www.leparisien.fr/resizer/eaSb-oLv-V9fDwjANeK5jnnPEas=/932x582/cloudfront-eu-central-1.images.arcpublishing.com/leparisien/ADZL22XDE5ALXIQXIC344M4LCA.jpg");
     
      
      setMessage("");
      setCurrentStep(1);
      setErrors({});
    }
  }, [open]);

  const validateStep1 = () => {
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Nom requis";
    if (!address.trim()) newErrors.address = "Adresse requise";
    if (!/^\d{8}$/.test(phone)) newErrors.phone = "Numéro invalide (8 chiffres)";
    if (!/^[A-Za-z0-9]{8}$/.test(matriculeFiscale)) 
      newErrors.matriculeFiscale = "7 chiffres + 1 caractères alphanumériques ";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!/^(https?:\/\/).+\.(jpe?g|png|webp)$/i.test(imageUrl)) 
      newErrors.imageUrl = "URL d'image invalide";
  
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    setCurrentStep(1);
    setErrors({});
    if (onClose) onClose();
  };



  const AddressDropdown = ({ address, setAddress, errors }) => {
    return (
      <Autocomplete
        options={tunisiaAddresses}
        value={address}
        onChange={(event, newValue) => setAddress(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label="Adresse"
            error={!!errors.address}
            helperText={errors.address}
            sx={{ mb: 3 }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <LocationOnIcon color="primary" />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  };

  const handleAddRestaurant = async () => {
    if (!currentUser) {
      setMessage("Utilisateur non authentifié.");
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "restaurants"), {
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        matriculeFiscale: matriculeFiscale.trim(),
        ownerId: currentUser.uid,
        imageUrl: imageUrl.trim(),
        
        createdAt: new Date(),
      });

      setMessage("Restaurant ajouté avec succès !");
      setCurrentStep(3);
      
      // Close after 2 seconds and refresh
      setTimeout(() => {
        handleClose();
        // Trigger a refresh in the parent component
        if (onClose) onClose(true);
      }, 2000);
    } catch (error) {
      setMessage(`Erreur: ${error.message}`);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              label="Nom du restaurant"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Business color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            <AddressDropdown address={address} setAddress={setAddress} errors={errors} />
            <TextField
              fullWidth
              label="Téléphone"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 8))}
              error={!!errors.phone}
              helperText={errors.phone || "8 chiffres (format tunisien)"}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Matricule Fiscale"
              value={matriculeFiscale}
              onChange={(e) => setMatriculeFiscale(e.target.value.slice(0, 8))}
              error={!!errors.matriculeFiscale}
              helperText={errors.matriculeFiscale || "7 chiffres + 1 caractères alphabetic"}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AssignmentInd color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => validateStep1() && setCurrentStep(2)}
                endIcon={<ArrowForward />}
                sx={{ borderRadius: 50, px: 4 }}
              >
                Suivant
              </Button>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              label="URL de l'image"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              error={!!errors.imageUrl}
              helperText={errors.imageUrl || "Ex: https://example.com/image.jpg"}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Link color="primary" />
                  </InputAdornment>
                ),
              }}
            />
            
            {imageUrl && (
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  src={imageUrl}
                  variant="rounded"
                  sx={{
                    width: 200,
                    height: 200,
                    margin: '0 auto',
                    boxShadow: theme.shadows[4],
                    border: errors.imageUrl ? `2px solid ${theme.palette.error.main}` : "none"
                  }}
                  onError={() => setErrors(prev => ({ ...prev, imageUrl: "Image non trouvée" }))}
                />
              </Box>
            )}

          

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => validateStep2() && handleAddRestaurant()}
              sx={{ borderRadius: 50, py: 1.5 }}
            >
              Enregistrer
            </Button>
          </Box>
        );
      case 3:
        return (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircle
              sx={{
                fontSize: 80,
                color: theme.palette.success.main,
                mb: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
              }}
            />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
              Succès !
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 4 }}>
              Le restaurant a été enregistré avec succès.
            </Typography>
            <Typography variant="body2" color="primary">
              Redirection...
            </Typography>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <UsageGuide 
        open={guideOpen} 
        onClose={() => setGuideOpen(false)} 
        onStart={() => {}}  // Removed handleOpen as it's not needed
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          fontWeight: 700,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4
        }}>
          Enregistrement de restaurant
        </DialogTitle>
        <DialogContent dividers>
          <Stepper
            activeStep={currentStep - 1}
            alternativeLabel
            sx={{ mb: 4, px: 2 }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ '& .MuiStepLabel-label': { fontWeight: 500 } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent()}
          {message && (
            <Typography color="primary" sx={{ mt: 2, textAlign: 'center' }}>
              {message}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {currentStep !== 3 && (
            <Button
              onClick={handleClose}
              color="secondary"
              sx={{ borderRadius: 50, px: 4 }}
            >
              Annuler
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddRestaurantFormPopup;